import type { FC, MouseEvent, RefObject } from 'react';
import React, { useState, useMemo, useCallback, cloneElement, useRef, useEffect } from 'react';
import { debounce } from 'debounce';
import { Stack, stackingOrder } from '@alfa-bank/site-stack';
import { Portal } from '@alfa-bank/site-portal';
import { Popper } from 'react-popper';
import type { VirtualElement, Options as PopperOptions, StrictModifiers } from '@popperjs/core';

import { ZoomerContext } from './zoomer-context';
import type { TPopover, TPopoverRef, TDOMEvent } from './models';
import { placementsMap } from './config';

const CLOSE_EVENT_TYPES = ['scroll', 'mousewheel', 'resize', 'touchmove'];

// TODO: Кажется, эта функциональность уже не нужна. Т.е. мы не используем zoom страницы.
/**
 * Функция используется, если Popover оказался в PageZoomer.
 * В этом случае включается упрощенный показ: поповер показывается
 * относительно курсора мыши, а не элемента, на который кликнули.
 */
function getVirtualRef({ x, y }: { x: number; y: number }) {
    return {
        getBoundingClientRect() {
            return {
                top: y,
                left: x,
                bottom: y,
                right: x,
                width: 0,
                height: 0,
            };
        },
        clientWidth: 0,
        clientHeight: 0,
    };
}

/**
 * Некоторые компоненты, как, например, Link, оборачивают callback `onClick` таким образом:
 * ```js
 * onClick={event => {
 *    if (onClick) {
 *       onClick({ event });
 *    }
 * }}
 * ```
 * Т. е. меняют интерфейс `onClick`.
 * Данная функция обрабатывает такие случаи.
 */
function findEvent(event: MouseEvent<HTMLElement> | { event: MouseEvent<HTMLElement> }) {
    return 'event' in event ? event.event : event;
}

/**
 * Компонент для показа всплывающей информации относительно компонента якоря: тултипов и меню
 *
 * Простой пример использования:
 * ```jsx
 * <Popover content={<div>Popover content</div>}>
 *  <button>Show popover</button>
 * </Popover>
 * ```
 *
 * Использование функции close:
 * ```jsx
 * <Popover content={(close) => <div onClick={close}>Close popover</div>}>
 *  <button>Show popover</button>
 * </Popover>
 * ```
 */

export const Popover: FC<React.PropsWithChildren<TPopover>> = ({
    ContentContainer,
    blockChild,
    boundariesElement = null,
    children,
    className,
    closeOnExterntalClick = true,
    content,
    contentContainerProps = {
        showCloseButton: true,
        maxWidth: 290,
    },
    createTree,
    dataTestId = 'popover',
    isHover,
    offset = 0,
    onClose,
    onOpen,
    placement = placementsMap.BOTTOM,
    tooltipHideTimeout,
    // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
    /**
     * DOM элемент соответствующий компоненту якорю
     */
    const targetRef = useRef<HTMLElement | null>(null);

    /**
     * DOM элемент соответствующий компоненту для содержания
     * поповера
     */
    const popoverRef = useRef<TPopoverRef>(null);

    /**
     * Упрощенный показ для PageZoomer-а
     */
    let zoomMode = false;

    /**
     * Искусственный DOM элемент для `zoomMode === true`
     */
    const virtualRef = useRef<VirtualElement>();

    // Скрипт не вывозит, что ниже есть использование, поэтому даёт ложные срабатывания
    // eslint-disable-next-line prefer-const
    let onBodyClick: (event: MouseEvent<HTMLElement>) => void;
    // eslint-disable-next-line prefer-const
    let close: (event: MouseEvent<HTMLElement>) => void;

    const [isShown, setShown] = useState(false);
    /**
     * Рефа для того, чтобы изменять и подтягивать актуальный стейт
     * в listeners, которые подвешиваются на body
     * https://medium.com/geographit/accessing-react-state-in-event-listeners-with-usestate-and-useref-hooks-8cceee73c559
     */
    const showPopoverRef = useRef(isShown);
    const setPopoverVisibility = (visible: boolean) => {
        showPopoverRef.current = visible;
        setShown(visible);
    };

    const removeEventListeners = useCallback(() => {
        /**
         * Реактовский ивент не приводится к ДОМовскому ивенту,
         * а он нужен для removeEventListener, поэтому сделано через unknown
         */
        document.body.removeEventListener('click', onBodyClick as unknown as TDOMEvent, false);

        CLOSE_EVENT_TYPES.forEach(type =>
            document.removeEventListener(type, close as unknown as TDOMEvent)
        );
    }, []);

    close = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            if (!showPopoverRef.current) {
                return;
            }
            setPopoverVisibility(false);
            removeEventListeners();
            if (onClose) {
                onClose(event);
            }
        },
        [onClose, removeEventListeners]
    );

    const debouncedClose = debounce(close, tooltipHideTimeout || 0);

    const addEventListeners = () => {
        /**
         * Реактовский ивент не приводится к ДОМовскому ивенту,
         * а он нужен для addEventListener, поэтому сделано через unknown
         */
        document.body.addEventListener('click', onBodyClick as unknown as TDOMEvent, false);

        CLOSE_EVENT_TYPES.forEach(type =>
            document.addEventListener(type, close as unknown as TDOMEvent)
        );
    };

    const open = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            if (showPopoverRef.current) {
                return;
            }

            setPopoverVisibility(true);
            addEventListeners();
            if (onOpen) {
                onOpen(event);
            }
        },
        [addEventListeners, onOpen]
    );

    onBodyClick = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            /**
             * Игнорируем клики на целевом компоненте,
             * иначе поповер не будет закрываться при клике
             * на целевой компонент
             */
            if (
                targetRef.current === event.target ||
                targetRef.current?.contains(event.target as HTMLElement)
            ) {
                return;
            }

            if (popoverRef.current && popoverRef.current.contains(event.target as HTMLElement)) {
                return;
            }

            if (!closeOnExterntalClick) {
                return;
            }

            close(event);
        },
        [targetRef, popoverRef, closeOnExterntalClick, close]
    );

    const [boundariesElem, setBoundariesElem] = useState<HTMLElement | null>(null);

    const getBoundariesElement = useCallback(() => {
        if (typeof boundariesElement === 'string') {
            return document.body.querySelector<HTMLElement>(boundariesElement);
        }

        if (boundariesElement instanceof HTMLElement) {
            return boundariesElement;
        }

        return null;
    }, [boundariesElement]);

    useEffect(() => {
        setBoundariesElem(getBoundariesElement());
    }, [getBoundariesElement]);

    const toggle = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            const foundEvent = findEvent(event);

            // TODO: Ошибка после миграции на typescript@4.4.4
            // @ts-ignore
            virtualRef.current = getVirtualRef({
                x: foundEvent.clientX,
                y: foundEvent.clientY,
            });

            if (showPopoverRef.current) {
                close(foundEvent);
            } else {
                open(foundEvent);
            }
        },
        [virtualRef, close, open]
    );

    const handleContentMouseOver = useCallback(() => {
        debouncedClose.clear();
    }, [debouncedClose]);

    const canUseTreeBuilder = useMemo(
        () => typeof content === 'object' && typeof createTree === 'function',
        [content, createTree]
    );

    const renderTarget = useMemo(() => {
        const childrenProps = isHover
            ? {
                  onMouseOver: open,
                  onMouseOut: debouncedClose,
              }
            : {
                  onClick: toggle,
              };

        return cloneElement(children, {
            ...childrenProps,
        });
    }, [children, debouncedClose, isHover, open, toggle]);

    const renderContent = useMemo(() => {
        if (typeof content === 'function') {
            return content({ close });
        }

        if (canUseTreeBuilder) return createTree ? createTree(content) : undefined;

        return content;
    }, [canUseTreeBuilder, close, content, createTree]);

    const modifiers = useMemo<StrictModifiers[]>(
        () => [
            {
                name: 'preventOverflow',
                options: {
                    boundary: boundariesElem || 'clippingParents',
                    altBoundary: true,
                },
            },
            {
                name: 'offset',
                options: {
                    offset: Array.isArray(offset)
                        ? offset
                        : [parseInt(offset as string, 10), parseInt(offset as string, 10)],
                },
            },
        ],
        [boundariesElem, offset]
    );

    return (
        <>
            {blockChild ? (
                <div
                    ref={targetRef as RefObject<HTMLDivElement>}
                    data-test-id={`${dataTestId}-target`}
                >
                    {renderTarget}
                </div>
            ) : (
                <span
                    ref={targetRef as RefObject<HTMLSpanElement>}
                    data-test-id={`${dataTestId}-target`}
                >
                    {renderTarget}
                </span>
            )}

            {isShown && targetRef.current && (
                <ZoomerContext.Consumer>
                    {zoom => {
                        zoomMode = zoom < 1;
                        const referenceElement = zoomMode ? virtualRef.current : targetRef.current!;

                        return (
                            <Stack value={stackingOrder.POSITIONER}>
                                {zIndex => (
                                    <Portal>
                                        <Popper
                                            modifiers={modifiers}
                                            placement={placement as PopperOptions['placement']}
                                            referenceElement={referenceElement}
                                        >
                                            {({ arrowProps, ref: getRef, style, ...rest }) => (
                                                // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
                                                <ContentContainer
                                                    innerRef={ref => {
                                                        popoverRef.current = ref;

                                                        // @ts-ignore
                                                        getRef(ref);
                                                    }}
                                                    onClose={close}
                                                    style={{
                                                        ...style,
                                                        zIndex,
                                                    }}
                                                    {...rest}
                                                    {...contentContainerProps}
                                                    arrowProps={{
                                                        ...arrowProps,
                                                        ...contentContainerProps.arrowProps,
                                                    }}
                                                    className={className}
                                                    dataTestId={dataTestId}
                                                    onMouseLeave={isHover ? close : undefined}
                                                    onMouseOver={handleContentMouseOver}
                                                >
                                                    {renderContent}
                                                </ContentContainer>
                                            )}
                                        </Popper>
                                    </Portal>
                                )}
                            </Stack>
                        );
                    }}
                </ZoomerContext.Consumer>
            )}
        </>
    );
};
